import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import ModalComponent from '../../../../common-components/ModalComponent/ModalComponent';
import FormGroup from '../../../../common-components/FormGroup/FormGroup';
import CustomSelect from '../../../inbound/components/CustomSelect';
import RichTextEditor from '../../../../common-components/RichtextEditor/RichTextEditor';
import ButtonComponent from '../../../../common-components/ButtonComponent/ButtonComponent';
import CustomTooltip from '../../../../common-components/CustomTooltip/CustomTooltip';
import {
  createSystemUsageRightsTemplateSchema,
  createUserUsageRightsTemplateSchema,
  updateSystemUsageRightsTemplateSchema,
  updateUserUsageRightsTemplateSchema,
} from '../../../../../validations/inboundEmailTemplate';
import {
  createInboundEmailTemplate,
  createUsageRightsTemplate,
  updateInboundEmailTemplate,
  updateSystemUsageRightsTemplate,
  updateUsageRightsTemplate,
} from '../../../../../services/inboundEmailTemplate.service';
import { fetchInboundEmailTemplates } from '../../../../../redux/user/userActions';
import { triggerGTMCtaClickedEvent } from '../../../../../utils/global';
import { CustomParamsHashmap } from '@/components/Dashboard/settings/components/InboundTemplate/customParam';
import CustomParamChip from '@/components/Dashboard/settings/components/InboundTemplate/CustomParamChip';
import { useCampaignDetail } from './CampaignDetailProvider';
import { css } from '@emotion/css';

interface FormState {
  templateName: string;
  body: string;
}

interface TemplateModalProps {
  show: boolean;
  onClose: () => void;
  template?: {
    _id: string;
    name: string;
    body: string;
    emailType: string;
  };
  mode?: 'create' | 'edit';
  showEmailSelect?: boolean;
  templateType?: 'system' | 'user';
  selectedTemplate?: any;
}

interface ValidationErrors {
  name?: string;
  body?: string;
}


const RteToolbarOption = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
  ],
};

const defaultFormState: FormState = { templateName: '', body: '' };


const TemplateModal: React.FC<TemplateModalProps> = ({
  show,
  onClose,
  template,
  mode = 'create',
  showEmailSelect = false,
  templateType = 'system',
  selectedTemplate,
}) => {
  const { connectedEmailList } = useSelector((state: any) => state.user);
  const dispatch = useDispatch();

  const customParams = useMemo(
    () => CustomParamsHashmap?.['usageRights'] || CustomParamsHashmap.default,
    [template]
  );

  // @ts-ignore
  const { setCurrentModal, setMessage, setSelectedTemplate } = useCampaignDetail();

  const [form, setForm] = useState<FormState>({ ...defaultFormState });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const validateData = async (body: any, action: 'create' | 'update'): Promise<boolean> => {
    let errorsData;
    const isCustomParams = checkValidCustomParams(body.subject || '', body.emailBody || '');
    if (!isCustomParams) {
      return false;
    }

    if (action === 'create') {
      if (templateType === 'system') {
        const { error } = createSystemUsageRightsTemplateSchema.validate(body, { abortEarly: false });
        errorsData = error;
      } else {
        const { error } = createUserUsageRightsTemplateSchema.validate(body, { abortEarly: false });
        errorsData = error;
      }
    } else {
      if (templateType === 'system') {
        const { error } = updateSystemUsageRightsTemplateSchema.validate(body, { abortEarly: false });
        errorsData = error;
      } else {
        const { error } = updateUserUsageRightsTemplateSchema.validate(body, { abortEarly: false });
        errorsData = error;
      }
    }

    if (errorsData) {
      const validationErrors: ValidationErrors = {};
      errorsData.details.forEach((detail: any) => {
        if (detail.context?.key) {
          validationErrors[detail.context.key as keyof ValidationErrors] = detail.message;
        }
      });
      setErrors(validationErrors);
      return false;
    }
    return true;
  };

  const checkValidCustomParams = (subject: string, body: string): boolean => {
    let isValid = true;
    const customParamText = customParams.map((p: string) => p.replace('{{', '').replace('}}', ''));

    const pattern = /{{(.*?)}}/g;
    const subjectMatches = subject.match(pattern);
    const matchedInSubject = subjectMatches ? subjectMatches.map((match) => match.replace(/{{|}}/g, '').trim()) : [];
    const bodyMatches = body.match(pattern);
    const matchedInBody = bodyMatches ? bodyMatches.map((match) => match.replace(/{{|}}/g, '').trim()) : [];

    for (const subjectCustomParam of matchedInSubject) {
      if (!customParamText.includes(subjectCustomParam)) {
        setErrors((prev) => ({
          ...prev,
          subject: `{{${subjectCustomParam}}} is not valid custom param.`
        }));
        isValid = false;
        break;
      }
    }

    for (const bodyCustomParam of matchedInBody) {
      if (!customParamText.includes(bodyCustomParam)) {
        setErrors((prev) => ({
          ...prev,
          emailBody: `{{${bodyCustomParam}}} is not valid custom param.`
        }));
        isValid = false;
        break;
      }
    }

    return isValid;
  };

  const onTemplateUpdate = async (): Promise<void> => {
    const body = {
      id: template?._id,
      name: form.templateName,
      body: form.body,
      emailType: template?.emailType,
    };

    const isValid = true

    if (isValid) {
      setIsLoading(true);
      let response;
      if (templateType === 'system') {
        response = await updateSystemUsageRightsTemplate(body);
      } else {
        response = await updateUsageRightsTemplate(body, template?._id);
      }
      if (response?.status) {
        NotificationManager.success('Changes Saved!', '', 1000);
        dispatch(fetchInboundEmailTemplates());
        modalClose();
        setErrors({});
        setMessage(response?.data?.body || '');
        setSelectedTemplate({ body: response?.data?.body, name: response?.data?.name, _id: response?.data?._id })
        setCurrentModal((prev: boolean) => !prev);
      } else {
        NotificationManager.error(response?.message || 'Something went wrong', '', 1200);
      }
    }
    setIsLoading(false);
  };

  const onTemplateCreate = async (): Promise<void> => {
    const body = {
      name: form.templateName,
      body: form.body,
      emailType: 'userTemplate',
    };

    const isValid = await validateData(body, 'create');
    if (isValid) {
      setIsLoading(true);
      const response = await createUsageRightsTemplate(body);
      if (response?.status) {
        NotificationManager.success('Template Created!', '', 1000);
        dispatch(fetchInboundEmailTemplates());
        modalClose();
        setErrors({});
        setMessage(response?.data?.body || '');
        setSelectedTemplate({ body: response?.data?.body, name: response?.data?.name, _id: response?.data?._id })
        setCurrentModal((prev: boolean) => !prev);
      } else {
        NotificationManager.error(response?.message || 'Something went wrong.', '', 1200);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (template) {
      setForm({
        templateName: template.name,
        body: template.body,
      });
    } else {
      setForm({ ...defaultFormState });
    }
  }, [template]);

  const quillRef = useRef<any>(null);

  const insertString = (stringToInsert: string): void => {
    const quill = quillRef.current?.getEditor();
    const selection = quill?.getSelection(true);
    if (selection) {
      quill?.insertText(selection.index, stringToInsert + ' ');
      setForm((prev) => ({ ...prev, body: quill?.root?.innerHTML || '' }));
    }
  };

  const modalClose = (): void => {
    setForm({ ...defaultFormState });
    setErrors({});
    setCurrentModal(null);
    onClose();
  };

  const modalTitle = useMemo(() => {
    if (mode === 'edit') return 'Edit Template';
    return 'Create New Template';
  }, [mode, template]);

  return (
    <ModalComponent show={show} onClose={modalClose} width={651} outsideClickClose={false} style={{ zIndex: 99999 }}>
      <div className="template-modal-component">
        <div className="template-modal-title">{modalTitle}</div>
        <CustomTooltip title="Name cannot be edited for system templates" show={templateType === 'system'} placement="top">
          <div>
            {/* @ts-ignore */}
            <FormGroup
              id="template-name"
              name="templateName"
              value={form.templateName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setForm((prev) => ({ ...prev, templateName: e.target.value }));
              }}
              rightLabelText={`${form.templateName.length}/50 Char.`}
              isError={form.templateName.length > 50}
              autoFocus
              label="Template Name"
              required
              placeholder="Enter a Name"
              errorText={errors.name}
              disabled={templateType === 'system'}
            />
          </div>
        </CustomTooltip>
        <div className="rte-label-container">
          <div className='text-subdued'>Body Text</div>
        </div>
        <span className="pt-usage-rights-template-leftLabel">{form.body.length}/200 Char</span>
        <RichTextEditor
          // @ts-ignore
          value={form.body}
          modules={RteToolbarOption}
          ref={quillRef}
          onChange={(v: string) => setForm((prev) => ({ ...prev, body: v }))}
          className={css`

            * {
              font-size: 14px;
            }

            .ql-toolbar {
                border-top-left-radius: 4px;
                border-top-right-radius: 4px;
            }

            .ql-container {
              height: 250px !important;
              border-bottom-left-radius: 4px;
              border-bottom-right-radius: 4px;
            }
          `}
        />
        {errors?.body && (
          <p style={{ color: 'var(--red)', textAlign: 'left', fontSize: 13, lineHeight: 1.4, paddingTop: '8px' }}>
            {errors.body}
          </p>
        )}
        <div className="custom-param-container">
          {customParams.map((text: string) => (
            <CustomParamChip
              key={text}
              text={text}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                insertString(`{{${text}}}`);
              }}
            />
          ))}
        </div>
        <div className="bottom-buttons">
          <ButtonComponent style={{ width: '100%' }} variant="secondary" onClick={modalClose}>
            Cancel
          </ButtonComponent>
          <ButtonComponent
            showLoading={isLoading}
            disabled={!form.templateName}
            onClick={() => (mode === 'edit' ? onTemplateUpdate() : onTemplateCreate())}
            style={{ width: '100%' }}>
            Save
          </ButtonComponent>
        </div>
      </div>
    </ModalComponent>
  );
};

export default TemplateModal;